// Combining this device's state with an account's saved state when someone signs in.
//
// Nothing is thrown away: histories (cooked, ratings, weights, check-ins) are unioned. Saved and
// hidden recipes keep each recipe's latest choice, so removing one on another device sticks.
// For settings, the device the person is signing in on wins once it has been set up, since that is
// what they just chose; a fresh device takes the account's settings. Allergies and diets are the
// union of both, so signing in can never drop a safety rule.

import { mergeUnlocks } from '../domain/plans';
import type { PersistedState } from './schema';
import { validate } from './persistence';

const uniq = <T,>(xs: T[], key: (x: T) => string): T[] => {
  const seen = new Set<string>();
  return xs.filter((x) => { const k = key(x); if (seen.has(k)) return false; seen.add(k); return true; });
};
const newestFirst = <T extends { at: string }>(xs: T[]) => [...xs].sort((a, b) => b.at.localeCompare(a.at));

/**
 * Saved and hidden recipes: the latest switch wins, per recipe. Without a timestamp on either side
 * (older data) the two are unioned, as before.
 */
function latestWins(kind: 'saved' | 'hidden', local: string[], remote: string[], lt: Record<string, string>, rt: Record<string, string>): string[] {
  return [...new Set([...local, ...remote])].filter((id) => {
    const l = lt[`${kind}:${id}`] ?? '';
    const r = rt[`${kind}:${id}`] ?? '';
    if (!l && !r) return true;
    return l >= r ? local.includes(id) : remote.includes(id);
  });
}
const latestStamps = (a: Record<string, string>, b: Record<string, string>) =>
  Object.fromEntries([...new Set([...Object.keys(a), ...Object.keys(b)])].map((k) => [k, (a[k] ?? '') > (b[k] ?? '') ? a[k] : b[k]]));

export function mergeStates(local: PersistedState, remote: PersistedState | null): PersistedState {
  if (!remote) return local;
  const base = local.onboarded ? local : remote;
  const other = local.onboarded ? remote : local;
  const merged: PersistedState = {
    ...base,
    onboarded: local.onboarded || remote.onboarded,
    onboardedAt: [local.onboardedAt, remote.onboardedAt].filter(Boolean).sort()[0] ?? null,
    profile: {
      ...base.profile,
      rules: [...new Set([...base.profile.rules, ...other.profile.rules])],
      dislikes: [...new Set([...base.profile.dislikes, ...other.profile.dislikes])],
      hiddenRecipes: latestWins('hidden', local.profile.hiddenRecipes, remote.profile.hiddenRecipes, local.toggledAt, remote.toggledAt),
      prompts: { ...base.profile.prompts, done: { ...other.profile.prompts.done, ...base.profile.prompts.done } },
    },
    kitchen: local.kitchen.items.length ? local.kitchen : remote.kitchen,
    plan: local.plan ?? remote.plan,
    planHistory: uniq([...local.planHistory, ...remote.planHistory], (p) => p.createdAt),
    list: local.list.length ? local.list : remote.list,
    saved: latestWins('saved', local.saved, remote.saved, local.toggledAt, remote.toggledAt),
    toggledAt: latestStamps(local.toggledAt, remote.toggledAt),
    cooked: newestFirst(uniq([...local.cooked, ...remote.cooked], (c) => `${c.recipeId}@${c.at}`)),
    feedback: newestFirst(uniq([...local.feedback, ...remote.feedback], (f) => `${f.type}@${f.at}`)),
    inferenceOverrides: { ...other.inferenceOverrides, ...base.inferenceOverrides },
    weights: uniq([...local.weights, ...remote.weights], (w) => w.date).sort((a, b) => a.date.localeCompare(b.date)),
    checkIns: newestFirst(uniq([...local.checkIns, ...remote.checkIns], (c) => c.at)),
    moreMealsDismissedAt: local.moreMealsDismissedAt ?? remote.moreMealsDismissedAt,
    recipeUnlocks: mergeUnlocks(local.recipeUnlocks, remote.recipeUnlocks),
  };
  // Re-validate so caps and shapes hold after the union.
  return validate(merged) ?? local;
}
